import { Box, Chip, Stack, Typography } from '@mui/material';
import HelpTrigger from '../../../help/components/HelpTrigger';
import type { Booking } from '../../types';
import {
  getAttendanceStatusMeta,
  getBookingStatusMeta,
  getPartialRefundStatusMeta,
  getPaymentStatusMeta,
  getRefundStatusMeta,
  resolveAttendanceStatus,
  resolveBookingStatus,
  resolvePartialRefundState,
  resolvePaymentStatus,
  resolveRefundStatus,
} from './reservationsAdmin.utils';

type Props = {
  booking: Booking;
};

export default function ReservationStatusStack({ booking }: Props) {
  const bookingStatus = resolveBookingStatus(booking);
  const paymentStatus = resolvePaymentStatus(booking);
  const attendanceStatus = resolveAttendanceStatus(booking);
  const refundStatus = resolveRefundStatus(booking);
  const partialRefundState = resolvePartialRefundState(booking);

  const attendanceMeta = getAttendanceStatusMeta(attendanceStatus, bookingStatus);
  const refundMeta = getRefundStatusMeta(refundStatus);
  const partialRefundMeta = getPartialRefundStatusMeta(partialRefundState);
  const bookingMeta = getBookingStatusMeta(bookingStatus);
  const paymentMeta = getPaymentStatusMeta(paymentStatus, bookingStatus);

  const chips = [
    ['confirmed', 'cancelled', 'pending_payment', 'pending_validation'].includes(bookingStatus)
      ? { key: 'booking', label: `Réservation ${bookingMeta.label}`, color: bookingMeta.color, variant: bookingMeta.variant }
      : null,
    ['paid', 'pending'].includes(paymentStatus)
      ? {
          key: 'payment',
          label: paymentStatus === 'paid' ? 'Paiement reçu' : 'Paiement à recevoir',
          color: paymentMeta.color,
          variant: paymentMeta.variant,
        }
      : null,
    partialRefundState === 'pending_manual' || partialRefundState === 'pending_customer_iban'
      ? {
          key: 'partial-refund',
          label: partialRefundMeta?.label || 'Remboursement à traiter',
          color: partialRefundMeta?.color || 'warning',
          variant: partialRefundMeta?.variant || 'outlined',
        }
      : refundStatus === 'pending' && refundMeta
        ? { key: 'refund', label: 'Remboursement à traiter', color: refundMeta.color, variant: refundMeta.variant }
        : null,
    attendanceStatus === 'expected' && attendanceMeta
      ? { key: 'attendance', label: 'Présence attendue', color: attendanceMeta.color, variant: attendanceMeta.variant }
      : null,
  ].filter(Boolean) as Array<{ key: string; label: string; color: any; variant: any }>;

  const refundText = partialRefundMeta?.label
    || refundMeta?.label
    || (booking.refundStatus === 'not_applicable' ? 'Aucun remboursement' : 'Aucun suivi en cours');

  const attendanceText = attendanceMeta?.label || 'Non suivi';

  function KeyValueRow({ label, value }: { label: string; value: string }) {
    return (
      <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, textAlign: 'right', overflowWrap: 'anywhere' }}>
          {value}
        </Typography>
      </Stack>
    );
  }

  const statusHelpArticleId = refundStatus === 'pending'
    ? 'booking.status.refund_pending'
    : bookingStatus === 'pending_validation'
      ? 'booking.status.pending_validation'
      : undefined;

  return (
    <Stack spacing={1.25} sx={{ minWidth: 0 }}>
      <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: '0.04em' }}>
          Statuts
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.25, color: 'text.secondary' }}>
          Badges réservés aux statuts qui demandent une lecture immédiate.
        </Typography>
      </Box>

      {chips.length > 0 ? (
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75}>
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              color={chip.color}
              variant={chip.variant}
              label={chip.label}
              sx={{
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
        </Stack>
      ) : null}

      <Stack spacing={0.75}>
        <KeyValueRow label="Réservation" value={bookingMeta.label} />
        <KeyValueRow label="Paiement" value={paymentMeta.label} />
        <KeyValueRow label="Remboursement" value={refundText} />
        <KeyValueRow label="Présence" value={attendanceText} />
        {booking.partialRefund?.ibanMasked ? (
          <KeyValueRow label="IBAN" value={booking.partialRefund.ibanMasked} />
        ) : null}
      </Stack>

      {statusHelpArticleId ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <HelpTrigger
            articleId={statusHelpArticleId}
            category="booking.status"
            label="Aide sur ce statut"
          />
        </Box>
      ) : null}
    </Stack>
  );
}
